import { clamp } from './utils.js'

export const historyMethods = {
  captureHistoryState() {
    const currentPage = this.visiblePages[this.currentIndex]
    return {
      state: this.serializeState(),
      currentIndex: this.currentIndex,
      currentPageId: currentPage?.id ?? null,
    }
  },

  recordHistory() {
    this.recordHistorySnapshot(this.captureHistoryState())
  },

  recordHistorySnapshot(snapshot) {
    if (!this.pdf || !snapshot || this.restoringHistory) return
    const previous = this.undoStack[this.undoStack.length - 1]
    if (!previous || previous.state !== snapshot.state) {
      this.undoStack.push(snapshot)
      if (this.undoStack.length > this.historyLimit) this.undoStack.shift()
    }
    this.redoStack = []
    this.updateHistoryControls()
  },

  clearHistory() {
    this.undoStack = []
    this.redoStack = []
    this.updateHistoryControls()
  },

  async undo() {
    if (!this.pdf || !this.undoStack.length) return
    const current = this.captureHistoryState()
    let target = this.undoStack.pop()
    while (target && target.state === current.state) target = this.undoStack.pop()
    if (!target) {
      this.updateHistoryControls()
      return
    }
    this.redoStack.push(current)
    await this.restoreHistoryState(target, 'Change undone.')
  },

  async redo() {
    if (!this.pdf || !this.redoStack.length) return
    const current = this.captureHistoryState()
    let target = this.redoStack.pop()
    while (target && target.state === current.state) target = this.redoStack.pop()
    if (!target) {
      this.updateHistoryControls()
      return
    }
    this.undoStack.push(current)
    await this.restoreHistoryState(target, 'Change redone.')
  },

  async restoreHistoryState(snapshot, message) {
    this.restoringHistory = true
    try {
      const existingPages: Map<string, any> = new Map(this.pages.map(page => [page.id, page]))
      const restoredPages = JSON.parse(snapshot.state)
      this.pages = restoredPages.map(page => ({
        ...existingPages.get(page.id),
        ...page,
      }))
      this.selectedTextItem = null
      this.selectedAnnotationId = null
      this.selectedMarkupId = null
      this.editingTextItemId = null
      this.editingAnnotationId = null
      this.focusInPlaceEditor = false
      this.focusAnnotationEditor = false
      this.annotationDrag = null
      this.markupInteraction = null
      this.resetTextEditor()

      const restoredVisibleIndex = this.visiblePages.findIndex(page => page.id === snapshot.currentPageId)
      this.currentIndex = restoredVisibleIndex >= 0
        ? restoredVisibleIndex
        : clamp(snapshot.currentIndex, 0, Math.max(0, this.visiblePages.length - 1))

      await this.renderThumbnails()
      if (this.visiblePages.length) {
        this.enablePageActions(true)
        await this.renderCurrentPage()
      } else {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.textEditLayer.innerHTML = ''
        this.annotationLayer.innerHTML = ''
        this.markupLayer.innerHTML = ''
        this.enablePageActions(false)
        this.updateCounters()
        this.updateDirtyState()
      }
    } finally {
      this.restoringHistory = false
    }
    this.updateHistoryControls()
    this.setStatus(message)
  },

  updateHistoryControls() {
    this.setToolbarDisabled(document.querySelector('[data-action="undo"]'), !this.pdf || !this.undoStack.length)
    this.setToolbarDisabled(document.querySelector('[data-action="redo"]'), !this.pdf || !this.redoStack.length)
  },
}
